// Workbook import flow + reconciliation/drift report (design §2.5). The
// modal owns the whole lifecycle for ONE chosen file: it POSTs on mount,
// then renders the full report for BOTH outcomes — 201 success and the 422
// reconciliation failure (same report shape, `imported: false`,
// `snapshot_id: null`, rolled back server-side).
//
// The 422 state is NOT a dead end: a client-edited re-upload routinely
// fails check 1 by exactly the unmapped total (§2.3's recalculated-file
// caveat), so the failure footer keeps the same File object and offers a
// "Force import" re-POST plus an optional expected-total override — no
// re-pick, and no native dialogs anywhere.

import React, { useEffect, useRef, useState } from 'react';
import { Modal, Tone, styles } from '../reconciliation/primitives';
import { importWorkbook, updateLine } from './api';
import './ImportReportModal.css';

function fmtMoney(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  const s = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `(${s})` : s;
}

function fmtCount(n) {
  return (n || 0).toLocaleString('en-US');
}

// Drift old/new values can be strings, lists (matter codes) or key objects
// (slide match_keys) — render them all readably.
function fmtValue(v) {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) {
    return v.map((x) => (typeof x === 'string' ? x : JSON.stringify(x))).join(', ') || '—';
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function detailText(body, fallback) {
  const d = body?.detail;
  if (typeof d === 'string') return d;
  if (d != null) return JSON.stringify(d);
  return fallback;
}

function OkBadge({ ok }) {
  if (ok == null) return <Tone tone="soft">skipped</Tone>;
  return ok ? <Tone tone="good">ties</Tone> : <Tone tone="miss">off</Tone>;
}

function Section({ title, count, open, children }) {
  return (
    <details className="irm-sec" open={open}>
      <summary>
        {title}
        {count != null && <span className="irm-count">{fmtCount(count)}</span>}
      </summary>
      <div className="irm-sec-body">{children}</div>
    </details>
  );
}

function ChecksTable({ rows, labelKey, valueKeys }) {
  return (
    <table className="irm-table">
      <thead>
        <tr>
          <th className="l">{labelKey.header}</th>
          {valueKeys.map((k) => (
            <th key={k.key}>{k.header}</th>
          ))}
          <th className="l" />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row[labelKey.key]}>
            <td className="l">{row[labelKey.key]}</td>
            {valueKeys.map((k) => (
              <td key={k.key} className="n">
                {fmtMoney(row[k.key])}
              </td>
            ))}
            <td className="l">
              <OkBadge ok={row.ok} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ImportReportModal({ file, fiscalYear, onClose, onImported, onToast }) {
  // Each force / expected-total re-submit bumps `n`; the effect below
  // re-POSTs the SAME File object with the attempt's flags.
  const [attempt, setAttempt] = useState({ n: 0, force: false, expectedTotal: '' });
  const [phase, setPhase] = useState('uploading'); // uploading | report | fatal
  const [report, setReport] = useState(null);
  const [fatal, setFatal] = useState(null);
  const [expectedInput, setExpectedInput] = useState('');
  const [expectedError, setExpectedError] = useState(false);
  const [closingId, setClosingId] = useState(null);
  const [closedIds, setClosedIds] = useState(() => new Set());
  // POST exactly once per attempt — without this, StrictMode's dev
  // remount re-runs the effect and a single file pick creates two
  // identical snapshots (the cancelled flag only suppresses setState,
  // not the request itself).
  const postedAttemptRef = useRef(-1);

  useEffect(() => {
    if (postedAttemptRef.current === attempt.n) return undefined;
    postedAttemptRef.current = attempt.n;
    let cancelled = false;
    setPhase('uploading');
    importWorkbook(file, fiscalYear, {
      expectedTotal: attempt.expectedTotal || undefined,
      force: attempt.force,
    })
      .then((res) => {
        if (cancelled) return;
        setReport(res.data);
        setPhase('report');
        if (res.data?.imported && onImported) onImported();
      })
      .catch((err) => {
        if (cancelled) return;
        const body = err.response?.data;
        if (
          err.response?.status === 422 &&
          body &&
          typeof body === 'object' &&
          'reconciled' in body
        ) {
          // Reconciliation failure — the body IS the full report.
          setReport(body);
          setPhase('report');
        } else {
          setFatal(detailText(body, err.message || 'Import failed'));
          setPhase('fatal');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [attempt, file, fiscalYear, onImported]);

  const handleForce = () => {
    // Sanitize before re-POSTing — a malformed number would otherwise hit
    // FastAPI's form validation (a 422 WITHOUT the report shape) and
    // dead-end the retry loop in the fatal state.
    const cleaned = expectedInput.replace(/[,$\s]/g, '');
    if (cleaned !== '' && Number.isNaN(Number(cleaned))) {
      setExpectedError(true);
      return;
    }
    setExpectedError(false);
    setAttempt((a) => ({ n: a.n + 1, force: true, expectedTotal: cleaned }));
  };

  const handleCloseLine = async (line) => {
    setClosingId(line.id);
    try {
      await updateLine(line.id, { status: 'closed' });
      setClosedIds((prev) => new Set(prev).add(line.id));
      onToast({ text: `Closed "${line.name}"`, tone: 'good' });
    } catch (err) {
      onToast({
        text: detailText(err.response?.data, err.message || 'Failed to close line'),
        tone: 'miss',
      });
    } finally {
      setClosingId(null);
    }
  };

  const uploading = phase === 'uploading';
  const r = report || {};
  const failed = phase === 'report' && !r.imported;
  const grandDelta = r.grand_delta != null ? Number(r.grand_delta) : null;
  const unmappedTotal = r.unmapped_total != null ? Number(r.unmapped_total) : null;
  const deltaIsUnmapped =
    grandDelta != null &&
    unmappedTotal != null &&
    unmappedTotal !== 0 &&
    Math.abs(Math.abs(grandDelta) - Math.abs(unmappedTotal)) < 0.005;

  // Per-tab rows: the ran checks, plus parsed-only tabs (no cached total —
  // e.g. Travel Other, folded into Travel's check) marked "skipped".
  const tabRows = [
    ...(r.tab_checks || []),
    ...Object.entries(r.per_tab_totals || {})
      .filter(([tab]) => !(r.tab_checks || []).some((c) => c.tab === tab))
      .map(([tab, imported]) => ({ tab, imported, cached: null, delta: null, ok: null })),
  ];

  const missingRows = r.missing_from_file || [];

  return (
    <Modal
      title={`Workbook import — FY${fiscalYear}`}
      subtitle={file.name}
      wide
      onClose={onClose}
      footer={
        <>
          {failed && !uploading && (
            <>
              <input
                className={`irm-expected${expectedError ? ' err' : ''}`}
                placeholder={`Expected total (e.g. ${fmtMoney(r.total)})`}
                title={expectedError ? 'Enter a plain number, e.g. 4220940.76' : undefined}
                value={expectedInput}
                onChange={(e) => {
                  setExpectedInput(e.target.value);
                  setExpectedError(false);
                }}
              />
              <button style={styles.primaryBtn} onClick={handleForce}>
                Force import
              </button>
            </>
          )}
          <span style={{ marginLeft: 'auto' }} />
          <button style={styles.ghostBtn} onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {uploading && (
        <div className="irm-uploading">
          <div className="t">Importing {file.name}…</div>
          <div className="s">
            Parsing the workbook, upserting lines and reconciling against the file's own
            cached totals.
          </div>
        </div>
      )}

      {phase === 'fatal' && (
        <div className="irm-banner fail">
          <div className="t">Import failed</div>
          <div className="s">{fatal}</div>
        </div>
      )}

      {phase === 'report' && (
        <>
          {r.imported ? (
            <div className="irm-banner ok">
              <div className="t">
                Imported — fresh FY{r.fiscal_year} snapshot created
                {r.snapshot_id ? ` (${r.snapshot_id.slice(0, 8)}…)` : ''}
              </div>
              <div className="s">{r.note}</div>
            </div>
          ) : (
            <div className="irm-banner fail">
              <div className="t">Reconciliation failed — rolled back, nothing persisted</div>
              <div className="s">
                Row counts below are parsed, not persisted. Re-submit the same file with
                “Force import”, optionally entering the workbook's true total as an
                expected-total override.
              </div>
            </div>
          )}

          <div className="irm-totals">
            <div className="cell">
              <span className="k">Parsed total</span>
              <span className="v">{fmtMoney(r.total)}</span>
            </div>
            <div className="cell">
              <span className="k">Workbook cached total</span>
              <span className="v">{fmtMoney(r.workbook_grand_total)}</span>
            </div>
            {r.expected_total != null && (
              <div className="cell">
                <span className="k">Expected total</span>
                <span className="v">{fmtMoney(r.expected_total)}</span>
              </div>
            )}
            <div className="cell">
              <span className="k">Grand Δ</span>
              <span className={`v${grandDelta ? ' off' : ''}`}>{fmtMoney(r.grand_delta)}</span>
            </div>
            <div className="cell">
              <span className="k">Unmapped total</span>
              <span className="v">{fmtMoney(r.unmapped_total)}</span>
            </div>
            <div className="cell">
              <span className="k">Reconciled</span>
              <span className="v">
                {r.reconciled ? <Tone tone="good">yes</Tone> : <Tone tone="miss">no</Tone>}
              </span>
            </div>
          </div>

          {failed && deltaIsUnmapped && (
            <div className="irm-hint">
              The grand delta matches the unmapped total — the file disagrees with itself by
              exactly the FP&amp;A rows that join no main-sheet line (a recalculated re-save
              does this). Force import is the routine path here.
            </div>
          )}

          <div className="irm-counts">
            <span>
              <strong>{fmtCount(r.lines_matched)}</strong> lines matched
            </span>
            <span>
              <strong>{fmtCount((r.lines_created || []).length)}</strong> created
            </span>
            <span>
              <strong>{fmtCount(r.matter_code_count)}</strong> matter codes
            </span>
            <span>
              <strong>{fmtCount(r.amount_rows)}</strong> amount rows
              {r.imported ? '' : ' (parsed)'}
            </span>
            <span>
              <strong>{fmtCount(r.zero_rows_skipped)}</strong> all-zero rows skipped
            </span>
            <span>
              <strong>{fmtCount(r.mappings_seeded)}</strong> vendor mappings seeded
            </span>
            <span>
              <strong>{fmtCount(r.slide_groups_seeded)}</strong> slide groups seeded
            </span>
          </div>

          {tabRows.length > 0 && (
            <Section title="Per-tab ties" count={tabRows.length} open={failed}>
              <ChecksTable
                rows={tabRows}
                labelKey={{ key: 'tab', header: 'Tab' }}
                valueKeys={[
                  { key: 'imported', header: 'Imported' },
                  { key: 'cached', header: 'Cached' },
                  { key: 'delta', header: 'Δ' },
                ]}
              />
            </Section>
          )}

          {(r.section_checks || []).length > 0 && (
            <Section title="Per-section ties (warnings only)" count={r.section_checks.length}>
              <ChecksTable
                rows={r.section_checks}
                labelKey={{ key: 'section', header: 'Section' }}
                valueKeys={[
                  { key: 'imported', header: 'Imported' },
                  { key: 'cached', header: 'Cached' },
                  { key: 'delta', header: 'Δ' },
                ]}
              />
            </Section>
          )}

          {(r.line_drift || []).length > 0 && (
            <Section title="Per-line drift vs cached totals" count={r.line_drift.length} open>
              <table className="irm-table">
                <thead>
                  <tr>
                    <th className="l">Line</th>
                    <th>Cached</th>
                    <th>Details</th>
                    <th>Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {r.line_drift.map((d) => (
                    <tr key={`${d.category}/${d.line}`}>
                      <td className="l">
                        <span className="irm-cat">{d.category}</span>
                        {d.line}
                      </td>
                      <td className="n">{fmtMoney(d.cached)}</td>
                      <td className="n">{fmtMoney(d.details)}</td>
                      <td className="n">{fmtMoney(d.delta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {(r.drift || []).length > 0 && (
            <Section title="Line field drift (updated from file)" count={r.drift.length}>
              <table className="irm-table">
                <thead>
                  <tr>
                    <th className="l">Line</th>
                    <th className="l">Field</th>
                    <th className="l">Old</th>
                    <th className="l">New</th>
                  </tr>
                </thead>
                <tbody>
                  {r.drift.map((d, i) => (
                    <tr key={`${d.line}-${d.field}-${i}`}>
                      <td className="l">
                        <span className="irm-cat">{d.category}</span>
                        {d.line}
                      </td>
                      <td className="l">{d.field}</td>
                      <td className="l old">{fmtValue(d.old)}</td>
                      <td className="l">{fmtValue(d.new)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {(r.lines_created || []).length > 0 && (
            <Section title="New lines" count={r.lines_created.length}>
              <ul className="irm-list">
                {r.lines_created.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </Section>
          )}

          {missingRows.length > 0 && (
            <Section title="In ERBI but absent from the file" count={missingRows.length} open>
              <div className="irm-note">
                Import supersedes web-entered amounts wholesale: each of these lines carries
                <strong> zero budget</strong> in the imported snapshot. Prior values stay in
                snapshot history and can be re-entered with a forecast save. Lines are never
                deleted or auto-closed — close one only if it is genuinely retired.
              </div>
              {missingRows.map((line) => {
                const closed = closedIds.has(line.id) || line.status === 'closed';
                return (
                  <div className="irm-missing" key={line.id}>
                    <span className="irm-cat">{line.category}</span>
                    <span className="nm">{line.name}</span>
                    {closed ? (
                      <Tone tone="soft">closed</Tone>
                    ) : (
                      <button
                        className="irm-close-line"
                        disabled={closingId === line.id}
                        onClick={() => handleCloseLine(line)}
                      >
                        {closingId === line.id ? 'Closing…' : 'Close line'}
                      </button>
                    )}
                  </div>
                );
              })}
            </Section>
          )}

          {(r.unmapped_gone_quiet || []).length > 0 && (
            <Section title="Unmapped lines gone quiet" count={r.unmapped_gone_quiet.length}>
              <div className="irm-note">
                These Unmapped (import) lines received no amounts this run — their orphan
                label left the file (e.g. a rename was healed). They go to zero in the new
                snapshot; no action needed.
              </div>
              <ul className="irm-list">
                {r.unmapped_gone_quiet.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </Section>
          )}

          {(r.unjoined_rows || []).length > 0 && (
            <Section title="FP&A rows joined to no line (imported as Unmapped)" count={r.unjoined_rows.length}>
              <ul className="irm-list">
                {r.unjoined_rows.map((row) => (
                  <li key={row}>{row}</li>
                ))}
              </ul>
            </Section>
          )}

          {(r.mapping_drift || []).length > 0 && (
            <Section title="Vendor-mapping drift" count={r.mapping_drift.length}>
              <div className="irm-note">
                Existing seed mappings now pointing at a different line than the file
                suggests — left untouched.
              </div>
              <ul className="irm-list">
                {r.mapping_drift.map((d) => (
                  <li key={d.vendor}>{d.vendor}</li>
                ))}
              </ul>
            </Section>
          )}

          {(r.slide_drift || []).length > 0 && (
            <Section title="Slide-group drift (seeds not applied)" count={r.slide_drift.length}>
              <div className="irm-note">
                Slide groups already exist for this FY, so the file's seeds were not written
                — these are the differences between the file and the saved groups.
              </div>
              <table className="irm-table">
                <thead>
                  <tr>
                    <th className="l">Group</th>
                    <th className="l">Field</th>
                    <th className="l">Saved</th>
                    <th className="l">File</th>
                  </tr>
                </thead>
                <tbody>
                  {r.slide_drift.map((d, i) => (
                    <tr key={`${d.name}-${d.field}-${i}`}>
                      <td className="l">{d.name}</td>
                      <td className="l">
                        {d.field === 'not_in_db'
                          ? 'in file only'
                          : d.field === 'not_in_file'
                          ? 'in ERBI only'
                          : d.field}
                      </td>
                      <td className="l old">{fmtValue(d.old)}</td>
                      <td className="l">{fmtValue(d.new)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {(r.warnings || []).length > 0 && (
            <Section title="Warnings" count={r.warnings.length} open>
              <ul className="irm-list warn">
                {r.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </Section>
          )}
        </>
      )}
    </Modal>
  );
}
